/* eslint-disable no-console */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Stack from 'react-bootstrap/Stack';
import Row from 'react-bootstrap/Row';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Carousel from 'react-multi-carousel';
import Button from 'react-bootstrap/Button';
import AnimateHeight from 'react-animate-height';
import { useSelector, shallowEqual } from 'react-redux';
import EmptyResult from '../EmptyResult';
import PostBlok from './PostBlok';
import i18next from '../../translations/i18n';

import 'react-multi-carousel/lib/styles.css';

import './index.scss';
import API from '../../services/API';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    partialVisibilityGutter: 50,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    partialVisibilityGutter: 50,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    partialVisibilityGutter: 50,
  },
};

const BlogPostsComponent = () => {
  const pageTopRef = useRef(null);
  const lastPostRef = useRef(null);
  const firstNewPostRef = useRef(null);

  const buildPostUrl = useCallback((uid) => {
    try {
      const { origin, pathname } = window.location;
      return `${origin}${pathname}?post=${encodeURIComponent(uid)}`;
    } catch (err) {
      console.error('buildPostUrl error', err);
      return `?post=${uid}`;
    }
  }, []);

  const handleShare = useCallback(
    async (uid) => {
      try {
        const shareUrl = buildPostUrl(uid);
        await navigator.clipboard.writeText(shareUrl);
        API.sendToGA('share_link');
      } catch (err) {
        console.error('handleShare error', err);
      }
    },
    [buildPostUrl],
  );
  const {
    filters,
    filteredPosts = [],
    stories: postsToPublish = [],
    sliderHeight,
  } = useSelector(
    (state) => ({
      filters: state.app.filters,
      filteredPosts: state.app.filteredPosts,
      stories: state.app.stories,
      sliderHeight: state.app.sliderHeight,
    }),
    shallowEqual, // Prevents re-renders if these specific values haven't changed
  );

  const [grid, setGrid] = useState(postsToPublish);
  const [itemOffset, setItemOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const itemsPerPage = parseInt(window.ttCentre.postsPerPage, 10) || 5;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = grid.slice(0, endOffset);
  const calculatePercentage = (index) =>
    Math.abs(Math.round(((index + 1) / postsToPublish.length) * 100));
  const [percentage, setPercentage] = useState(calculatePercentage(0));

  const updatedFilters = filters
    .filter((f) => f.checked)
    .map((filter) => filter.name)
    .join(', ');

  const handleLoadMore = () => {
    if (endOffset < grid.length) {
      setItemOffset(endOffset);
    }
    API.sendToGA(i18next.t('loadMore'));
  };

  const handleShowLess = () => {
    if (itemOffset - itemsPerPage >= 0) {
      setItemOffset(itemOffset - itemsPerPage);
    }
    API.sendToGA(i18next.t('loadLess'));
  };

  const getFeature = (posts) => {
    return posts.map((postObj) => {
      const uid = postObj._uid;
      return (
        <div
          id={`post-${uid}`}
          key={uid}
          className="post-item position-relative"
        >
          <PostBlok post={postObj} handleShare={handleShare} isSmallDevice />
        </div>
      );
    });
  };

  const bringPostToFront = (basePosts) => {
    const params = new URLSearchParams(window.location.search);
    const paramUid = params.get('post');
    const hash = window.location.hash || '';
    const hashUid = hash.startsWith('#post-')
      ? hash.replace('#post-', '')
      : null;
    const targetUid = paramUid || hashUid;

    if (targetUid) {
      const targetIndex = basePosts.findIndex((p) => p._uid === targetUid);
      if (targetIndex > -1) {
        return [
          basePosts[targetIndex],
          ...basePosts.slice(0, targetIndex),
          ...basePosts.slice(targetIndex + 1),
        ];
      }
    }
    return basePosts;
  };

  useEffect(() => {
    const init = async () => {
      if (filteredPosts.length > 0 || updatedFilters) {
        setGrid(filteredPosts);
      } else {
        setGrid(postsToPublish);
      }
    };

    init();
  }, [filteredPosts]);

  useEffect(() => {
    const basePosts =
      filteredPosts.length > 0 || updatedFilters
        ? filteredPosts
        : postsToPublish;

    setGrid(bringPostToFront(basePosts));
  }, [filteredPosts, updatedFilters, postsToPublish]);
  return (
    <div>
      {/*  Mobiles only */}
      <div className="d-block d-md-none post">
        {currentItems.length > 0 && (
          <>
            <Carousel
              rewind
              showDots={false}
              keyBoardControl
              partialVisible
              itemClass="image-item"
              sliderClass=""
              responsive={responsive}
              afterChange={() => {}}
              beforeChange={(event) => {
                setPercentage(calculatePercentage(event));
              }}
            >
              {getFeature(
                bringPostToFront(
                  filteredPosts.length > 0 || updatedFilters
                    ? filteredPosts
                    : postsToPublish,
                ),
              )}
            </Carousel>
            <Stack className="mt-5">
              <ProgressBar
                now={percentage}
                variant="info"
                className="p-0 mx-3"
              />
            </Stack>
          </>
        )}
        {currentItems.length === 0 && (
          <EmptyResult
            checkedFilters={updatedFilters}
            message={i18next.t('NoResultFoundMsg')}
          />
        )}
      </div>

      {/* Medium and desktop devices */}
      <div className="desktop d-none d-md-block post" ref={pageTopRef}>
        {currentItems.length > 0 && (
          <AnimateHeight
            duration={500}
            height={open ? 'auto' : sliderHeight}
            onHeightAnimationEnd={() => true}
          >
            <div id="collapse-text">
              <Row className="gap-2 ">
                {currentItems.map((col, index) => {
                  const uid = col._uid;
                  let refValue;
                  if (index === currentItems.length - 1) {
                    refValue = lastPostRef;
                  } else if (index === itemOffset) {
                    refValue = firstNewPostRef;
                  } else {
                    refValue = null;
                  }
                  return (
                    <div
                      key={`${uid}`}
                      id={`post-${uid}`}
                      ref={refValue}
                      className="mb-2 position-relative"
                      style={undefined}
                    >
                      <PostBlok
                        post={col}
                        key={`${uid}`}
                        isSmallDevice={false}
                        handleShare={handleShare}
                      />
                    </div>
                  );
                })}
              </Row>

              <section className="mt-5 d-flex justify-content-center gap-5 mb-3">
                {/* Show Less button */}
                {itemOffset > 0 && (
                  <Button
                    onClick={handleShowLess}
                    className="slider_button btn btn-teal"
                    variant="teal"
                  >
                    {i18next.t('loadLess')}
                  </Button>
                )}

                {itemOffset <= 0 && (
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      API.sendToGA(
                        `${open ? i18next.t('seeLess') : i18next.t('seeMore')}`,
                      );
                      pageTopRef.current.scrollIntoView();
                      setOpen(!open);
                    }}
                    className="slider_button btn btn-teal"
                    variant="teal"
                  >
                    {open ? i18next.t('seeLess') : i18next.t('seeMore')}
                  </Button>
                )}

                {/* Load More button */}
                {endOffset < grid.length && (
                  <Button
                    onClick={handleLoadMore}
                    className="slider_button btn btn-teal"
                    variant="teal"
                  >
                    {i18next.t('loadMore')}
                  </Button>
                )}
              </section>
            </div>
          </AnimateHeight>
        )}
        {currentItems.length === 0 && (
          <EmptyResult
            checkedFilters={updatedFilters}
            message={i18next.t('NoResultFoundMsg')}
          />
        )}
        {currentItems.length > 1 && !open && (
          <Stack
            className={`justify-content-center d-flex post__slider-close hstack z-3 ${
              open ? 'post__slider-open' : ''
            }`}
          >
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                API.sendToGA(
                  `${open ? i18next.t('seeLess') : i18next.t('seeMore')}`,
                );
                pageTopRef.current.scrollIntoView();
                setOpen(!open);
              }}
              aria-expanded={open}
              variant="teal"
              size="lg"
              className={`slider_button btn btn-teal px-4 ${
                !open ? 'view-btn' : ''
              }`}
              title={!open ? `${i18next.t('seeMore')}` : ''}
            >
              {open ? i18next.t('seeLess') : i18next.t('seeMore')}
            </Button>
          </Stack>
        )}
      </div>
    </div>
  );
};
const BlogPosts = React.memo(BlogPostsComponent);
BlogPosts.displayName = 'BlogPosts';
BlogPosts.defaultProps = {
  postsToPublish: [],
};
BlogPosts.propTypes = {};

export default BlogPosts;
